import type { Point2, Segment2, Vec3, ViewBasis } from './viewTypes'

// ---------------------------------------------------------------------------
// Remoção de linhas ocultas (HLR) por MAPA DE PROFUNDIDADE.
//
// O problema: a projecção desenha todas as arestas dentro da
// profundidade da vista, mesmo as que estão tapadas por outro elemento.
// Num corte profundo ou numa rede de MEP densa isso dá um emaranhado.
//
// A abordagem exacta (recortar cada aresta contra cada triângulo, com
// ordenação por profundidade) é O(arestas x triângulos) e não acaba num
// modelo a sério. Faz-se o que os motores fazem: RASTERIZA-SE uma vez o
// modelo todo num mapa de profundidade em espaço de vista e depois cada
// aresta é AMOSTRADA contra esse mapa. O custo passa a ser linear nos
// triângulos mais linear no comprimento das arestas.
//
// Espaço de vista, não de ecrã: a projecção é ortográfica, portanto
// (x, y) são directamente as coordenadas do desenho em metros e a
// profundidade `w` é AFIM em (x, y) dentro de cada triângulo. Não há
// divisão perspéctica nem interpolação hiperbólica: interpola-se o w
// linearmente e está certo.
//
// PLANO DE CORTE (near). Tudo o que está aquém do plano (w < 0) foi
// cortado fora e NÃO pode tapar nada: os triângulos são recortados a
// w >= 0 antes de entrarem no mapa. Sem isto, numa planta, o topo das
// paredes (que fica acima do corte) tapava o piso todo.
//
// VIÉS (bias) PROPORCIONAL AO DECLIVE, o erro clássico deste algoritmo.
// Uma aresta de silhueta está SEMPRE exactamente à profundidade do
// triângulo que a gerou, portanto sem folga apagava-se a si própria. E a
// folga não pode ser constante: junto ao contorno de um tubo a
// superfície está quase de perfil e a profundidade varia muito dentro de
// UMA célula, enquanto numa laje horizontal não varia nada. Guarda-se
// por isso `w + viés` com
//
//     viés = viesConst + viesDeclive * célula * max(|dw/dx|, |dw/dy|)
//
// que é a fórmula clássica do "polygon offset" (unidades + declive).
// Como a lacuna a cobrir dentro de uma célula é precisamente
// `declive x célula`, um `viesDeclive` acima de 1 chega; usa-se 2 para
// ter margem na diagonal. O viés é limitado (`maxBiasCells`) para uma
// faceta quase de perfil não abrir um buraco na cena.
//
// Tudo aqui é puro: números e arrays tipados, sem three e sem DOM.
// ---------------------------------------------------------------------------

/** Segmento já projectado, com a profundidade nos extremos. */
export interface SegmentW {
  a: Point2
  b: Point2
  /** Profundidade do extremo `a`, ao longo do viewDir. */
  wa: number
  /** Profundidade do extremo `b`. */
  wb: number
}

export interface DepthMapOptions {
  /** Orçamento de células do mapa. 4 milhões = 16 MB em Float32. */
  maxCells?: number
  /** Célula mínima, em metros (não vale a pena descer abaixo da
   *  resolução do papel). */
  minCellM?: number
  /** Multiplicador do viés proporcional ao declive. */
  slopeBias?: number
  /** Viés fixo, em metros. */
  constBias?: number
  /** Tecto do viés, em células. */
  maxBiasCells?: number
}

export const DEPTH_MAX_CELLS = 4_000_000
export const DEPTH_MIN_CELL_M = 0.002
export const DEPTH_SLOPE_BIAS = 2
export const DEPTH_CONST_BIAS = 1e-5
export const DEPTH_MAX_BIAS_CELLS = 32

/** Mapa de profundidade em espaço de vista. `data[i]` é a profundidade
 *  MAIS PRÓXIMA (já com o viés somado) que cobre a célula `i`, ou
 *  `Infinity` se nada a cobre. */
export interface DepthMap {
  minX: number
  minY: number
  cell: number
  nx: number
  ny: number
  data: Float32Array
  /** Viés fixo, guardado para o teste de visibilidade poder usá-lo. */
  constBias: number
  /** Triângulos que entraram no mapa (diagnóstico). */
  triangles: number
}

export interface Extent2D {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Cria um mapa vazio que cobre `extent` com uma margem de uma célula.
 *  A grelha respeita o aspecto da extensão e o orçamento de células. */
export function createDepthMap(
  extent: Extent2D,
  opts?: DepthMapOptions,
): DepthMap | null {
  const w = extent.maxX - extent.minX
  const h = extent.maxY - extent.minY
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 0 || h < 0) return null
  const maxCells = Math.max(1024, opts?.maxCells ?? DEPTH_MAX_CELLS)
  const minCell = Math.max(1e-6, opts?.minCellM ?? DEPTH_MIN_CELL_M)
  // Extensão útil: uma dimensão nula (desenho degenerado) não pode
  // levar a divisão por zero.
  const ew = Math.max(w, minCell)
  const eh = Math.max(h, minCell)
  // Célula que cabe no orçamento: nx*ny <= maxCells com nx/ny = ew/eh.
  let cell = Math.sqrt((ew * eh) / maxCells)
  if (!Number.isFinite(cell) || cell <= 0) return null
  if (cell < minCell) cell = minCell
  const nx = Math.min(Math.ceil(ew / cell) + 2, 1 << 15)
  const ny = Math.min(Math.ceil(eh / cell) + 2, 1 << 15)
  if (nx * ny > maxCells * 4) return null
  const data = new Float32Array(nx * ny)
  data.fill(Infinity)
  return {
    minX: extent.minX - cell,
    minY: extent.minY - cell,
    cell,
    nx,
    ny,
    data,
    constBias: opts?.constBias ?? DEPTH_CONST_BIAS,
    triangles: 0,
  }
}

/** Extensão da projecção de uma caixa de mundo, pelos 8 cantos. */
export function projectedExtent(
  box: { min: Vec3; max: Vec3 },
  basis: ViewBasis,
): Extent2D {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (let i = 0; i < 8; i++) {
    const dx = (i & 1 ? box.max[0] : box.min[0]) - basis.origin[0]
    const dy = (i & 2 ? box.max[1] : box.min[1]) - basis.origin[1]
    const dz = (i & 4 ? box.max[2] : box.min[2]) - basis.origin[2]
    const x = dx * basis.right[0] + dy * basis.right[1] + dz * basis.right[2]
    const y = dx * basis.up[0] + dy * basis.up[1] + dz * basis.up[2]
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

/** Vértice projectado usado pela rasterização. */
interface PV {
  x: number
  y: number
  w: number
}

/** Rasteriza os triângulos de uma malha no mapa. As posições estão no
 *  MESMO referencial que a `basis` (ver a nota de precisão do
 *  `generateView2D`). Devolve quantos triângulos entraram. */
export function rasterizeMesh(
  map: DepthMap,
  positions: Float32Array | number[],
  indices: Uint32Array | Uint16Array | number[] | null,
  basis: ViewBasis,
  opts?: DepthMapOptions,
): number {
  if (!positions || positions.length < 9) return 0
  const triCount = indices
    ? Math.floor(indices.length / 3)
    : Math.floor(positions.length / 9)
  if (triCount < 1) return 0
  const slopeBias = opts?.slopeBias ?? DEPTH_SLOPE_BIAS
  const constBias = opts?.constBias ?? map.constBias
  const maxBias = (opts?.maxBiasCells ?? DEPTH_MAX_BIAS_CELLS) * map.cell
  const o = basis.origin
  const r = basis.right
  const u = basis.up
  const v = basis.viewDir

  const pv = (vi: number): PV => {
    const dx = positions[vi * 3] - o[0]
    const dy = positions[vi * 3 + 1] - o[1]
    const dz = positions[vi * 3 + 2] - o[2]
    return {
      x: dx * r[0] + dy * r[1] + dz * r[2],
      y: dx * u[0] + dy * u[1] + dz * u[2],
      w: dx * v[0] + dy * v[1] + dz * v[2],
    }
  }

  let done = 0
  const poly: PV[] = []
  for (let t = 0; t < triCount; t++) {
    const i0 = indices ? indices[t * 3] : t * 3
    const i1 = indices ? indices[t * 3 + 1] : t * 3 + 1
    const i2 = indices ? indices[t * 3 + 2] : t * 3 + 2
    poly.length = 0
    poly.push(pv(i0), pv(i1), pv(i2))
    const clipped = clipNear(poly)
    if (clipped.length < 3) continue
    // Leque a partir do primeiro vértice: o recorte a um plano dá sempre
    // um polígono CONVEXO, portanto o leque é válido.
    for (let k = 1; k + 1 < clipped.length; k++) {
      if (
        rasterTriangle(
          map,
          clipped[0],
          clipped[k],
          clipped[k + 1],
          slopeBias,
          constBias,
          maxBias,
        )
      ) {
        done++
      }
    }
  }
  map.triangles += done
  return done
}

/** Recorta o polígono ao semi-espaço w >= 0 (Sutherland-Hodgman num só
 *  plano). O que está aquém do plano de corte não tapa nada. */
function clipNear(poly: PV[]): PV[] {
  let allIn = true
  let allOut = true
  for (const p of poly) {
    if (p.w < 0) allIn = false
    else allOut = false
  }
  if (allIn) return poly
  if (allOut) return []
  const out: PV[] = []
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]
    const b = poly[(i + 1) % poly.length]
    const ain = a.w >= 0
    const bin = b.w >= 0
    if (ain) out.push(a)
    if (ain !== bin) {
      const t = a.w / (a.w - b.w)
      out.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
        w: 0,
      })
    }
  }
  return out
}

/** Rasteriza um triângulo. Devolve false quando não toca célula
 *  nenhuma (área projectada nula ou fora do mapa). */
function rasterTriangle(
  map: DepthMap,
  p0: PV,
  p1: PV,
  p2: PV,
  slopeBias: number,
  constBias: number,
  maxBias: number,
): boolean {
  const { cell, nx, ny, data, minX, minY } = map
  // Coordenadas em células.
  const ax = (p0.x - minX) / cell
  const ay = (p0.y - minY) / cell
  const bx = (p1.x - minX) / cell
  const by = (p1.y - minY) / cell
  const cx = (p2.x - minX) / cell
  const cy = (p2.y - minY) / cell
  const area = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax)
  if (!Number.isFinite(area) || Math.abs(area) < 1e-12) return false

  // Plano da profundidade: w = w0 + gx*(x-x0) + gy*(y-y0), em CÉLULAS.
  const w1 = p1.w - p0.w
  const w2 = p2.w - p0.w
  const gx = (w1 * (cy - ay) - w2 * (by - ay)) / area
  const gy = (w2 * (bx - ax) - w1 * (cx - ax)) / area
  let bias = constBias + slopeBias * Math.max(Math.abs(gx), Math.abs(gy))
  if (!Number.isFinite(bias) || bias < 0) bias = maxBias
  if (bias > maxBias) bias = maxBias

  let x0 = Math.floor(Math.min(ax, bx, cx))
  let x1 = Math.ceil(Math.max(ax, bx, cx))
  let y0 = Math.floor(Math.min(ay, by, cy))
  let y1 = Math.ceil(Math.max(ay, by, cy))
  if (x1 < 0 || y1 < 0 || x0 >= nx || y0 >= ny) return false
  if (x0 < 0) x0 = 0
  if (y0 < 0) y0 = 0
  if (x1 > nx - 1) x1 = nx - 1
  if (y1 > ny - 1) y1 = ny - 1

  const inv = 1 / area
  let touched = false
  for (let iy = y0; iy <= y1; iy++) {
    const py = iy + 0.5
    const row = iy * nx
    for (let ix = x0; ix <= x1; ix++) {
      const px = ix + 0.5
      // Coordenadas baricêntricas pelo sinal das áreas.
      const l0 =
        ((bx - px) * (cy - py) - (by - py) * (cx - px)) * inv
      if (l0 < -1e-9) continue
      const l1 =
        ((cx - px) * (ay - py) - (cy - py) * (ax - px)) * inv
      if (l1 < -1e-9) continue
      const l2 = 1 - l0 - l1
      if (l2 < -1e-9) continue
      const w = p0.w + gx * (px - ax) + gy * (py - ay) + bias
      const i = row + ix
      if (w < data[i]) data[i] = w
      touched = true
    }
  }
  return touched
}

/** Profundidade guardada na célula que contém (x, y). `Infinity` se
 *  estiver fora do mapa ou se nada cobrir a célula. */
export function depthAt(map: DepthMap, x: number, y: number): number {
  const ix = Math.floor((x - map.minX) / map.cell)
  if (ix < 0 || ix >= map.nx) return Infinity
  const iy = Math.floor((y - map.minY) / map.cell)
  if (iy < 0 || iy >= map.ny) return Infinity
  return map.data[iy * map.nx + ix]
}

export interface OccludeOptions {
  /** Passo da amostragem, em células. */
  stepCells?: number
  /** Tecto de amostras por segmento (segmentos muito longos). */
  maxSamples?: number
}

export const OCCLUDE_STEP_CELLS = 1
export const OCCLUDE_MAX_SAMPLES = 4096

/** Devolve os troços VISÍVEIS de um segmento contra o mapa.
 *
 *  Amostra o segmento de célula a célula e parte-o nas transições, em
 *  vez de o deitar fora inteiro: uma conduta que passa por trás de um
 *  pilar aparece dos dois lados dele, como num desenho a sério. */
export function occludeSegment(
  map: DepthMap,
  seg: SegmentW,
  opts?: OccludeOptions,
): Segment2[] {
  const dx = seg.b[0] - seg.a[0]
  const dy = seg.b[1] - seg.a[1]
  const len = Math.hypot(dx, dy)
  if (!Number.isFinite(len)) return []
  const step = (opts?.stepCells ?? OCCLUDE_STEP_CELLS) * map.cell
  const maxN = opts?.maxSamples ?? OCCLUDE_MAX_SAMPLES
  // Nº de intervalos: pelo menos 1, para um segmento mais curto do que a
  // célula continuar a ser testado nos dois extremos.
  const n = Math.max(1, Math.min(maxN, Math.ceil(len / Math.max(step, 1e-9))))

  const visibleAt = (t: number): boolean => {
    const x = seg.a[0] + dx * t
    const y = seg.a[1] + dy * t
    const w = seg.wa + (seg.wb - seg.wa) * t
    return w <= depthAt(map, x, y)
  }

  const out: Segment2[] = []
  let runStart: number | null = visibleAt(0) ? 0 : null
  let prev = runStart !== null
  for (let i = 1; i <= n; i++) {
    const t = i / n
    const vis = visibleAt(t)
    if (vis === prev) continue
    // Transição a meio caminho entre as duas amostras: é o melhor
    // palpite sem ir procurar a intersecção exacta, e o erro fica abaixo
    // de meia célula (invisível ao traço do desenho).
    const mid = t - 0.5 / n
    if (vis) {
      runStart = mid
    } else if (runStart !== null) {
      pushRun(out, seg, runStart, mid)
      runStart = null
    }
    prev = vis
  }
  if (prev && runStart !== null) pushRun(out, seg, runStart, 1)
  return out
}

function pushRun(
  out: Segment2[],
  seg: SegmentW,
  t0: number,
  t1: number,
): void {
  if (!(t1 > t0)) return
  const dx = seg.b[0] - seg.a[0]
  const dy = seg.b[1] - seg.a[1]
  const a: Point2 = [seg.a[0] + dx * t0, seg.a[1] + dy * t0]
  const b: Point2 = [seg.a[0] + dx * t1, seg.a[1] + dy * t1]
  if (a[0] === b[0] && a[1] === b[1]) return
  out.push({ a, b })
}

/** Aplica a remoção a uma lista de segmentos. */
export function occludeSegments(
  map: DepthMap,
  segs: SegmentW[],
  opts?: OccludeOptions,
): Segment2[] {
  const out: Segment2[] = []
  for (const s of segs) {
    const parts = occludeSegment(map, s, opts)
    for (const p of parts) out.push(p)
  }
  return out
}
